import axios from 'axios'
import authSession from '@/lib/authSession'

export type TicketStatus = 'open' | 'awaiting_support' | 'awaiting_customer' | 'resolved' | 'archived'
export type TicketPriority = 'p0' | 'p1' | 'p2' | 'rfe'
export type TicketCategory = 'technical_support' | 'billing' | 'account' | 'server_performance' | 'connectivity' | 'abuse_report' | 'general_question' | 'rfe'

export interface SupportAttachment { public_id: string; filename: string; mime_type: string; file_size: number }
export interface SupportMessage { public_id: string; author_uuid?: string; type: 'customer' | 'admin' | 'internal_note' | 'system'; body: string; created_at: string; attachments: SupportAttachment[] }
export interface SupportService { server_uuid: string; service_name: string; service_type?: string; node_name?: string; region?: string; service_status: string; power_state?: string; cancelled: boolean; cancelled_at?: string }
export interface SupportCustomer { uuid: string; name?: string; email: string; username?: string }
export interface SupportTicket {
  public_id: string; ticket_number: string; subject: string; category: TicketCategory; priority: TicketPriority
  status: TicketStatus; assigned_admin_uuid?: string | null; related_service_uuid?: string | null
  service?: SupportService | null; customer_uuid?: string; customer?: SupportCustomer; created_at: string; updated_at: string
  resolved_at?: string | null; archived_at?: string | null; messages?: SupportMessage[]; activity?: Record<string, unknown>[]
}
export interface SupportAdmin { uuid: string; name: string; email: string; role: 'sre' | 'admin'; permissions: string[] }
export interface Pagination { page: number; per_page: number; total: number; pages?: number }
export interface SupportCustomerDetail { uuid: string; display_name: string; email: string; username: string; created_at: string; account_status: string; activity_status: 'active' | 'stale' | 'inactive'; email_verified: boolean; two_factor_enabled: boolean; server_count: number }
export interface SupportServerDetail { uuid: string; name: string; owner: { uuid: string; display_name: string; email: string; username: string }; game: string; type: string; region?: string; node_display_name?: string; public_address?: string | null; configured: { cpu_percent: number; memory_mb: number; disk_mb: number }; current: { cpu_percent: number | null; memory_mb: number | null; disk_mb: number | null }; install_state: string; suspended: boolean; power_state: string; created_at: string }
export interface OperationalLogEntry {
  scope?: 'server' | 'node'
  id: string; timestamp: string; level: string; event_code: string; service: string
  node_uuid: string | null; server_uuid: string | null; message: string
  fields: Record<string, string | number | boolean | null>
}
export interface OperationalLogs {
  server_uuid: string; node: { uuid: string | null; name: string | null }
  window: { from: string; to: string }
  coverage: { available: boolean; configured: boolean; complete: boolean; start: string | null; end: string | null; reason: string | null; results_complete?: boolean; results_reason?: string; source_state?: string }
  entries: OperationalLogEntry[]; next_cursor: string | null
}
export interface OperationalLogQuery {
  from: string; to: string; limit?: number; cursor?: string; service?: string; level?: string
}
export interface OperationalCodeLocation { module: string; function: string; line: number }
export interface SystemOperationalLogEntry extends Omit<OperationalLogEntry, 'scope' | 'fields'> {
  scope: 'system'
  fields: Record<string, string | number | boolean | null | OperationalCodeLocation[]>
}
export interface OperationalSourceHealth {
  source_id: string; configured: boolean; last_seen_at: string | null
  freshness: 'fresh' | 'stale' | 'missing' | 'unavailable' | 'unconfigured'
  coverage_state: 'observed' | 'partial' | 'unknown'
  reason: string; evidence: 'heartbeat' | 'observation' | null
}
export interface SystemOperationalLogs {
  scope: 'system'; window: OperationalLogs['window']
  filters: Record<string, string | null>
  options: { services: string[]; components: string[]; sources: string[]; source_ids: string[]; levels: string[] }
  coverage: OperationalLogs['coverage'] & {
    sources: OperationalSourceHealth[]
    source_health_results_complete?: boolean
    source_health_results_reason?: 'complete' | 'truncated' | 'invalid_entries' | 'unavailable'
  }
  entries: SystemOperationalLogEntry[]; next_cursor: string | null
}
export interface SystemOperationalLogQuery extends OperationalLogQuery {
  component?: string; source?: string; source_id?: string
}
export interface SupportInvoiceSummary { invoice_number?: string; status: string; currency: string; created_at?: string; due_at?: string; paid_at?: string; subtotal: number; total: number; amount_paid: number; amount_due: number }
export interface SupportQueueResult { tickets: SupportTicket[]; queue_counts: Record<string, number>; pagination: Pagination }
export interface CreateTicketResult { public_id: string; ticket_number: string; message_id: string; ticket: SupportTicket }

const client = axios.create({ baseURL: '', timeout: 20000, headers: { Accept: 'application/json' } })
client.interceptors.request.use(async config => {
  const token = await authSession.getValidAccessToken()
  if (token.status === 'available') config.headers.Authorization = `Bearer ${token.accessToken}`
  return config
})
client.interceptors.response.use(response => response, async error => {
  const original = error.config as typeof error.config & { _authRetry?: boolean }
  if (error.response?.status === 401 && !original?._authRetry) {
    original._authRetry = true
    const refreshed = await authSession.refreshAccessToken({
      failedAccessToken: authSession.getAccessToken(),
    })
    if (refreshed.status === 'available') {
      original.headers = original.headers || {}
      original.headers.Authorization = `Bearer ${refreshed.accessToken}`
      return client(original)
    }
    if (refreshed.status === 'expired') authSession.expireSession()
  }
  return Promise.reject(error)
})

function data<T>(response: unknown): T { return (response as { data: { data: T } }).data.data }
export function supportError(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) return String(error.response?.data?.error || fallback)
  return fallback
}
function multipart(payload: Record<string, unknown>, files: File[]) {
  const form = new FormData()
  form.append('payload', JSON.stringify(payload))
  files.forEach(file => form.append('files', file))
  return form
}

export const supportApi = {
  async list(page = 1, perPage = 25) { return data<{ tickets: SupportTicket[]; summary: { open: number; awaiting_response: number; awaiting_customer: number; resolved: number; total: number }; pagination: Pagination }>(await client.get('/hoard-api/support/tickets', { params: { page, per_page: perPage } })) },
  async services() { return data<SupportService[]>(await client.get('/hoard-api/support/services')) },
  async get(id: string) { return data<SupportTicket>(await client.get(`/hoard-api/support/tickets/${id}`)) },
  async create(payload: Record<string, unknown>, files: File[]) { return data<CreateTicketResult>(await client.post('/hoard-api/support/tickets', multipart(payload, files))) },
  async reply(id: string, body: string, files: File[]) { return data<{ message_id: string; status: TicketStatus }>(await client.post(`/hoard-api/support/tickets/${id}/messages`, multipart({ body }, files))) },
  async resolve(id: string) { return data<SupportTicket>(await client.post(`/hoard-api/support/tickets/${id}/resolve`)) },
  async reopen(id: string) { return data<SupportTicket>(await client.post(`/hoard-api/support/tickets/${id}/reopen`)) },
  downloadUrl(id: string, admin = false) { return `/hoard-api/${admin ? 'admin/support' : 'support'}/attachments/${id}/download` },
  async adminSession() { return data<{ role: 'user' | 'sre' | 'admin'; permissions: string[]; is_support_admin: boolean; ready: boolean; force_password_change: boolean; two_factor_enabled: boolean }>(await client.get('/hoard-api/admin/support/session')) },
  async adminList(params: Record<string, unknown>) { return data<SupportQueueResult>(await client.get('/hoard-api/admin/support/tickets', { params })) },
  async adminGet(id: string) { return data<SupportTicket>(await client.get(`/hoard-api/admin/support/tickets/${id}`)) },
  async adminServices(id: string) { return data<SupportService[]>(await client.get(`/hoard-api/admin/support/tickets/${id}/services`)) },
  async adminReply(id: string, body: string, files: File[]) { return data<{ message_id: string; status: TicketStatus }>(await client.post(`/hoard-api/admin/support/tickets/${id}/messages`, multipart({ body }, files))) },
  async adminNote(id: string, body: string, files: File[]) { return data<{ message_id: string; status: TicketStatus }>(await client.post(`/hoard-api/admin/support/tickets/${id}/notes`, multipart({ body }, files))) },
  async adminUpdate(id: string, patch: Record<string, unknown>) { return data<SupportTicket>(await client.patch(`/hoard-api/admin/support/tickets/${id}`, patch)) },
  async assign(id: string, admin_uuid: string | null) { await client.post(`/hoard-api/admin/support/tickets/${id}/assign`, { admin_uuid }) },
  async transition(id: string, action: 'resolve' | 'reopen' | 'archive') { return data<SupportTicket>(await client.post(`/hoard-api/admin/support/tickets/${id}/${action}`)) },
  async administrators() { return data<SupportAdmin[]>(await client.get('/hoard-api/admin/support/administrators')) },
  async customers(params: Record<string, unknown> = {}) { return data<{ customers: SupportCustomerDetail[]; pagination: Pagination }>(await client.get('/hoard-api/admin/support/customers', { params })) },
  async customer(uuid: string) { return data<SupportCustomerDetail>(await client.get(`/hoard-api/admin/support/customers/${uuid}`)) },
  async customerServers(uuid: string, params: Record<string, unknown> = {}) { return data<{ servers: SupportServerDetail[]; pagination: Pagination }>(await client.get(`/hoard-api/admin/support/customers/${uuid}/servers`, { params })) },
  async customerInvoices(uuid: string) { return data<{ invoices: SupportInvoiceSummary[]; has_more: boolean }>(await client.get(`/hoard-api/admin/support/customers/${uuid}/invoices`)) },
  async servers(params: Record<string, unknown> = {}) { return data<{ servers: SupportServerDetail[]; pagination: Pagination }>(await client.get('/hoard-api/admin/support/servers', { params })) },
  async server(uuid: string) { return data<SupportServerDetail>(await client.get(`/hoard-api/admin/support/servers/${uuid}`)) },
  async operationalLogs(uuid: string, params: OperationalLogQuery, signal?: AbortSignal) {
    try {
      return data<OperationalLogs>(await client.get(`/hoard-api/admin/support/servers/${uuid}/operational-logs`, { params, signal }))
    } catch (error) {
      // A structured unavailable receipt is not an empty successful search.
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        const payload = error.response.data?.data as OperationalLogs | undefined
        if (payload?.coverage?.available === false && Array.isArray(payload.entries)) return payload
      }
      throw error
    }
  },
  async systemOperationalLogs(params: SystemOperationalLogQuery, signal?: AbortSignal) {
    try {
      return data<SystemOperationalLogs>(await client.get('/hoard-api/admin/support/operational-logs', { params, signal }))
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 503) {
        const payload = error.response.data?.data as SystemOperationalLogs | undefined
        if (payload?.scope === 'system' && payload.coverage?.available === false && Array.isArray(payload.entries)) return payload
      }
      throw error
    }
  },
}

export const categoryLabels: Record<TicketCategory, string> = {
  technical_support: 'Technical Support', billing: 'Billing', account: 'Account', server_performance: 'Server Performance',
  connectivity: 'Connectivity', abuse_report: 'Abuse Report', general_question: 'General Question',
  rfe: 'RFE (Request for Enhancement)',
}
export const statusLabels: Record<TicketStatus, string> = { open: 'Open', awaiting_support: 'Waiting on Support', awaiting_customer: 'Waiting on Customer', resolved: 'Resolved', archived: 'Archived' }
export const customerStatusLabels: Record<TicketStatus, string> = { open: 'Open', awaiting_support: 'Waiting on Metrly', awaiting_customer: 'Waiting on You', resolved: 'Resolved', archived: 'Archived' }
export const priorityLabels: Record<TicketPriority, string> = {
  p0: 'P0 — Urgent / Critical',
  p1: 'P1 — Normal',
  p2: 'P2 — Not Urgent',
  rfe: 'RFE — Request for Enhancement',
}
